/**
 * Règles de cumul par motif (disciplinary_accumulation_rules) côté webapp :
 * mapping des lignes et petites règles d'affichage. Le comptage lui-même vit
 * côté serveur (get_motif_accumulation_status, _shared/disciplinary/motifAccumulation.ts).
 */

/** Sanction types a motif rule can count (card_types). Generated OTHER rows never count. */
export const MOTIF_CARD_TYPES: ReadonlySet<string> = new Set(["YELLOW_CARD", "RED_CARD_DIRECT", "RED_CARD_TWO_YELLOWS"]);

export interface AccumulationRule {
    id: string;
    leagueId: string;
    seasonFrom: string;
    seasonTo: string | null;
    /** Cartons comptés : YELLOW_CARD, RED_CARD_DIRECT, RED_CARD_TWO_YELLOWS. */
    cardTypes: string[];
    infractionIds: string[];
    threshold: number;
    suspensionMatches: number;
    repeats: boolean;
    reasonId: string;
    articleCode: string;
    labelAr: string;
    labelFr: string;
    notes: string | null;
    createdBy: string | null;
    createdAt: string;
    closedBy: string | null;
    closedAt: string | null;
}

export interface MotifCountedCard {
    sanctionId: string;
    type: string;
    matchId: string | null;
    decisionDate: string;
    reasonId: string | null;
    /** Cards this sanction is worth (2 for a second yellow for repeated protest). */
    weight: number;
}

export interface MotifIssuedSanction {
    sanctionId: string;
    threshold: number;
    matchId: string | null;
    caseId: string | null;
    published: boolean;
}

export interface MotifStatusRow {
    ruleId: string;
    playerId: string;
    teamId: string | null;
    counted: number;
    threshold: number;
    /** Next threshold to reach; null once a non-repeating rule has fired. */
    nextThreshold: number | null;
    countedCards: MotifCountedCard[];
    issued: MotifIssuedSanction[];
    /** Issued sanctions whose threshold is no longer reached (count went down). */
    caduc: number;
    player: { firstName: string | null; lastName: string | null; firstNameAr: string | null; lastNameAr: string | null };
    team: { name: string; name_ar: string | null } | null;
    /** Season licence (subject_licenses). */
    licenseNumber: string | null;
    /** players.license_number — season-less, search fallback only. */
    legacyLicenseNumber: string | null;
}

/** What reconcile-yellow-commissions reports as created (onlyMotif). */
export interface MotifCreated {
    sanctionId: string;
    playerId: string;
    ruleId: string;
    threshold: number;
    articleCode: string;
    suspensionMatches: number;
}

export type RuleStatus = "active" | "closed" | "upcoming";

export function mapAccumulationRule(row: any): AccumulationRule {
    return {
        id: row.id,
        leagueId: row.league_id,
        seasonFrom: row.season_from,
        seasonTo: row.season_to ?? null,
        cardTypes: Array.isArray(row.card_types) ? row.card_types : [],
        infractionIds: Array.isArray(row.infraction_ids) ? row.infraction_ids : [],
        threshold: row.threshold,
        suspensionMatches: row.suspension_matches,
        repeats: !!row.repeats,
        reasonId: row.reason_id,
        articleCode: row.article_code,
        labelAr: row.label_ar,
        labelFr: row.label_fr,
        notes: row.notes ?? null,
        createdBy: row.created_by ?? null,
        createdAt: row.created_at,
        closedBy: row.closed_by ?? null,
        closedAt: row.closed_at ?? null,
    };
}

export function mapMotifStatusRow(row: any): MotifStatusRow {
    return {
        ruleId: row.rule_id,
        playerId: row.player_id,
        teamId: row.team_id ?? null,
        counted: row.counted ?? 0,
        threshold: row.threshold,
        nextThreshold: row.next_threshold ?? null,
        countedCards: (row.counted_cards ?? []).map((c: any) => ({
            sanctionId: c.sanction_id,
            type: c.type,
            matchId: c.match_id ?? null,
            decisionDate: c.decision_date,
            reasonId: c.reason_id ?? null,
            weight: c.weight ?? 1,
        })),
        issued: (row.issued ?? []).map((c: any) => ({
            sanctionId: c.sanction_id,
            threshold: c.threshold,
            matchId: c.match_id ?? null,
            caseId: c.case_id ?? null,
            published: !!c.published,
        })),
        caduc: row.caduc ?? 0,
        player: {
            firstName: row.player_first_name ?? null,
            lastName: row.player_last_name ?? null,
            firstNameAr: row.player_first_name_ar ?? null,
            lastNameAr: row.player_last_name_ar ?? null,
        },
        team: row.team_name ? { name: row.team_name, name_ar: row.team_name_ar ?? null } : null,
        licenseNumber: row.license_number ?? null,
        legacyLicenseNumber: row.legacy_license_number ?? null,
    };
}

/** Where a rule stands relative to a season (season codes compare lexically). */
export function ruleStatus(rule: Pick<AccumulationRule, "seasonFrom" | "seasonTo">, season: string): RuleStatus {
    if (rule.seasonFrom > season) return "upcoming";
    if (rule.seasonTo != null && rule.seasonTo < season) return "closed";
    return "active";
}

export function ruleAppliesToSeason(rule: Pick<AccumulationRule, "seasonFrom" | "seasonTo">, season: string): boolean {
    return ruleStatus(rule, season) === "active";
}

export interface MotifChipView {
    key: string;
    article: string;
    label: string;
    near: boolean;
}

/** Chips per player for the match entry, one per rule with a counted card. */
export function motifChipsByPlayer(rows: MotifStatusRow[], rules: AccumulationRule[]): Map<string, MotifChipView[]> {
    const article = new Map(rules.map((r) => [r.id, r.articleCode]));
    const out = new Map<string, MotifChipView[]>();
    for (const row of rows) {
        const code = article.get(row.ruleId);
        if (!code) continue;
        const chip = motifChip(row, code);
        if (!chip) continue;
        const list = out.get(row.playerId) ?? [];
        list.push({ key: row.ruleId, article: code, ...chip });
        out.set(row.playerId, list);
    }
    return out;
}

/** « Art. 71 : 3/4 » — near when the next card reaches a threshold. */
export function motifChip(row: MotifStatusRow, articleCode: string): { label: string; near: boolean } | null {
    if (row.counted <= 0) return null;
    if (row.nextThreshold == null) return { label: `Art. ${articleCode} : ${row.counted}`, near: false };
    return {
        label: `Art. ${articleCode} : ${row.counted}/${row.nextThreshold}`,
        near: row.nextThreshold - row.counted === 1,
    };
}

const isNear = (r: MotifStatusRow) => r.nextThreshold != null && r.nextThreshold - r.counted === 1;

/** Caduc first, then one card from a threshold, then by count. */
export function sortMotifStatusRows(rows: MotifStatusRow[]): MotifStatusRow[] {
    return [...rows].sort(
        (a, b) =>
            Number(b.caduc > 0) - Number(a.caduc > 0) ||
            Number(isNear(b)) - Number(isNear(a)) ||
            b.counted - a.counted ||
            a.playerId.localeCompare(b.playerId),
    );
}

/** Bell items: a generated suspension not yet in a published PV, or a caduc one. */
export function pendingMotifRows(rows: MotifStatusRow[]): MotifStatusRow[] {
    return rows.filter((r) => r.caduc > 0 || r.issued.some((i) => !i.published));
}
